import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { CheckCircle2, ArrowLeft } from 'lucide-react';
import api from '../api';
import { extractErrorMessage } from '../lib/errors';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Response is the same whether the email exists — we still show the
      // confirmation to prevent account enumeration.
      const r = await api.post('/auth/forgot-password', { email: email.trim() });
      toast.success(r.data.message || 'Check your inbox');
      setSent(true);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Could not send reset email'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0b] flex items-center justify-center px-5">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <div className="w-9 h-9 rounded-full border border-[#d4a94a] flex items-center justify-center">
            <span className="font-display text-[#d4a94a] text-xl font-semibold leading-none">R</span>
          </div>
          <span className="font-display text-2xl text-zinc-100">Reachly</span>
        </Link>
        <Card className="bg-[#111110] border-zinc-900 p-8" data-testid="forgot-password-card">
          {sent ? (
            <div className="text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-[#1a1815] border border-[#3a2f18] flex items-center justify-center text-[#f5d67a] mb-4">
                <CheckCircle2 size={22}/>
              </div>
              <h1 className="font-display text-2xl text-zinc-100">Check your inbox.</h1>
              <p className="text-sm text-zinc-400 mt-3 leading-relaxed">
                If an account matches <span className="text-zinc-200">{email}</span>, we've sent a reset link. It's good for 60 minutes and can only be used once. Peek in your spam folder if you don't see it in a minute.
              </p>
              <Button
                variant="outline"
                className="mt-6 border-zinc-800 text-zinc-200 hover:bg-zinc-900"
                onClick={() => { setSent(false); setEmail(''); }}
                data-testid="forgot-send-another"
              >
                Send to a different address
              </Button>
              <div className="mt-4">
                <Link to="/login" className="text-sm text-zinc-500 hover:text-[#f5d67a] inline-flex items-center gap-1">
                  <ArrowLeft size={14}/>Back to sign in
                </Link>
              </div>
            </div>
          ) : (
            <>
              <h1 className="font-display text-2xl text-zinc-100">Reset your password.</h1>
              <p className="text-sm text-zinc-500 mt-1">Enter your account email and we'll send a link.</p>
              <form onSubmit={onSubmit} className="mt-6 space-y-4">
                <div>
                  <Label className="text-zinc-300">Email</Label>
                  <Input
                    type="email" required autoFocus
                    value={email} onChange={e=>setEmail(e.target.value)}
                    className="mt-1.5 bg-[#0a0a0b] border-zinc-800 text-zinc-100 focus-visible:ring-[#d4a94a]"
                    placeholder="you@example.com"
                    data-testid="forgot-email-input"
                  />
                </div>
                <Button
                  type="submit" disabled={loading}
                  className="w-full bg-[#d4a94a] hover:bg-[#e5bb5c] text-black font-medium"
                  data-testid="forgot-submit-btn"
                >
                  {loading ? 'Sending…' : 'Send reset link'}
                </Button>
              </form>
              <div className="mt-6 text-sm text-zinc-500 text-center">
                Remembered it? <Link to="/login" className="text-[#f5d67a] hover:underline">Back to sign in</Link>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
